import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, XCircle, Loader2, MailWarning, MailOpen, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/useAuth';

const API_BASE_URL = (import.meta.env.VITE_BACKEND_URL || import.meta.env.VITE_PUBLIC_URL || '').replace(/\/$/, '');

// 'error': no answer about the link at all (offline, a 5xx, rate limited).
// Unlike 'invalid' it says nothing about the link, so it offers a retry.
type State = 'checking' | 'ok' | 'expired' | 'invalid' | 'pending' | 'error';

// What the link in the URL is (2026-09-25). Three kinds reach this page:
//   * ?token=      the old SMTP email's link (GET, unchanged);
//   * ?oobCode=    Firebase's own link, when the console's action URL points
//                  here (&mode=verifyEmail&apiKey=...&lang=...);
//   * ?fv=         the Continue button on Firebase's hosted page, which has
//                  already applied the code: the server asks Firebase whether
//                  the address is verified now ("not yet" is the pending state).
type VerifyLink =
  | { kind: 'token'; token: string }
  | { kind: 'oob'; oobCode: string; mode: string | null }
  | { kind: 'fv'; fv: string };

/** The page's state for a link, and whether it is the server's final word on it. */
interface Answer {
  state: State;
  final: boolean;
}

async function askServer(link: VerifyLink): Promise<Answer> {
  let res: Response;
  try {
    res = link.kind === 'token'
      ? await fetch(`${API_BASE_URL}/api/auth/verify-email?token=${encodeURIComponent(link.token)}`)
      : await fetch(`${API_BASE_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          link.kind === 'oob' ? { oob_code: link.oobCode, mode: link.mode || undefined } : { fv: link.fv },
        ),
      });
  } catch {
    return { state: 'error', final: false };
  }
  const body = await res.json().catch(() => ({}));
  if (res.ok && body.ok) return { state: 'ok', final: true };
  if (body.error === 'token_expired') return { state: 'expired', final: true };
  if (body.error === 'not_verified_yet') return { state: 'pending', final: true };
  // A 5xx (verification_failed: Firebase or Mongo unreachable), a rate
  // limit, a timeout: the link may be perfectly good. Ask again later.
  if (res.status >= 500 || res.status === 429 || res.status === 408) return { state: 'error', final: false };
  // invalid_token, missing_token, unsupported_action (a password-reset
  // link sent here by the custom action URL).
  return { state: 'invalid', final: true };
}

// One request per link however often the page mounts: an action code and an
// old token are both spent by their first use, so a second request (React's
// development double mount, a remount) would turn a success into "invalid".
// Only the server's final answers are kept (2026-09-25 review): a failed
// request is no answer, and keeping it showed a good link as dead until a
// full reload.
const checks = new Map<string, Promise<State>>();

function remember(key: string, question: Promise<Answer>): Promise<State> {
  const entry: Promise<State> = question.then(({ state, final }) => {
    if (!final && checks.get(key) === entry) checks.delete(key);
    return state;
  });
  checks.set(key, entry);
  return entry;
}

function checkOnce(link: VerifyLink): Promise<State> {
  const key = JSON.stringify(link);
  return checks.get(key) ?? remember(key, askServer(link));
}

/** The signed-in account's own address, when it is a real one (not a studio placeholder). */
function accountEmail(user: { email?: string | null; studio_account?: boolean } | null | undefined): string | null {
  const email = String(user?.email || '').trim();
  if (!email || user?.studio_account || !email.includes('@') || /\.invalid$/i.test(email)) return null;
  return email;
}

// Where the button in the confirmation email lands. Until this page existed the
// link fell through to the 404 route, so every confirmation email was a dead
// end even on the days one actually went out.
//
// Verification gates NOTHING — the account already works. This page confirms
// the address and offers a re-send when the 24h token has run out.
export default function VerifyEmail() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const token = params.get('token');
  const oobCode = params.get('oobCode');
  const mode = params.get('mode');
  const fv = params.get('fv');
  const [state, setState] = useState<State>('checking');
  const [resent, setResent] = useState(false);
  const [rechecking, setRechecking] = useState(false);

  const link: VerifyLink | null = token
    ? { kind: 'token', token }
    : oobCode
      ? { kind: 'oob', oobCode, mode }
      : fv
        ? { kind: 'fv', fv }
        : null;
  const linkKey = link ? JSON.stringify(link) : '';

  useEffect(() => {
    if (!link) {
      setState('invalid');
      return;
    }
    let cancelled = false;
    setState('checking');
    checkOnce(link).then((next) => {
      if (!cancelled) setState(next);
    });
    return () => {
      cancelled = true;
    };
    // linkKey stands for link (a new object on every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkKey]);

  // "Check again" after opening the emailed link, or "Try again" after no
  // answer: a fresh question, not the remembered answer.
  const checkAgain = async () => {
    if (!link || rechecking) return;
    setRechecking(true);
    const next = await remember(linkKey, askServer(link));
    setState(next);
    setRechecking(false);
  };

  const resend = async () => {
    // Signed in: the account's own address, no need to ask for it.
    const email = accountEmail(user) || (window.prompt(t('verifyEmail.resendPrompt')) || '').trim();
    if (!email) return;
    try {
      await fetch(`${API_BASE_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, lang: i18n.language }),
      });
    } catch {
      // The endpoint always answers ok; a network blip is not worth alarming
      // anyone over when nothing is blocked either way.
    }
    setResent(true);
  };

  const icon = {
    checking: <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />,
    ok: <Check className="h-8 w-8 text-green-600" />,
    expired: <MailWarning className="h-8 w-8 text-amber-500" />,
    invalid: <XCircle className="h-8 w-8 text-muted-foreground" />,
    pending: <MailOpen className="h-8 w-8 text-amber-500" />,
    error: <TriangleAlert className="h-8 w-8 text-amber-500" />,
  }[state];

  return (
    <div className="flex min-h-[80vh] items-center justify-center bg-background p-4 text-foreground">
      <div
        data-testid={`verify-email-${state}`}
        className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center text-card-foreground shadow-sm"
      >
        <div className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full ${state === 'ok' ? 'bg-green-100' : 'bg-muted'}`}>
          {icon}
        </div>
        <h1 className="mb-2 text-2xl font-bold">{t(`verifyEmail.${state}Title`)}</h1>
        <p className="mb-6 text-muted-foreground">{t(`verifyEmail.${state}Body`)}</p>

        {(state === 'pending' || state === 'error') && (
          <button
            type="button"
            onClick={checkAgain}
            disabled={rechecking}
            className="mb-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
          >
            {rechecking && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {t(state === 'error' ? 'verifyEmail.retryCta' : 'verifyEmail.checkAgainCta')}
          </button>
        )}

        {(state === 'expired' || state === 'invalid' || state === 'pending' || state === 'error') && (
          <button
            type="button"
            onClick={resend}
            disabled={resent}
            className="mb-4 w-full rounded-lg border border-input px-4 py-2 text-sm font-semibold hover:bg-accent disabled:opacity-60"
          >
            {resent ? t('verifyEmail.resentConfirmation') : t('verifyEmail.resendCta')}
          </button>
        )}

        <Link
          to="/"
          className="inline-block rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
        >
          {t('verifyEmail.continueCta')}
        </Link>
      </div>
    </div>
  );
}
